export type VersionRecordType = 'member' | 'subscription' | 'condominium' | 'entity'
const VERSION_ROUTE: Record<VersionRecordType, string> = { member: 'members', subscription: 'subscriptions', condominium: 'condominiums', entity: 'entities' }

/**
 * Registre des routes de l'API.
 *
 * Les URLs étaient écrites en dur dans une vingtaine de pages : renommer une
 * route serveur imposait de fouiller tout `app/`. Elles sont ici regroupées par
 * ressource, ce qui donne aussi une vue de ce que le client consomme.
 *
 * Le registre ne fait que construire des chemins : le chargement (clé de cache,
 * rafraîchissement, filtres) reste dans les pages, qui en ont chacune un usage
 * différent.
 */

type Id = number | string

export const api = {
  members: {
    list: () => '/api/admin/members',
    batchDelete: () => '/api/admin/members/batch-delete',
    byId: (id: Id) => `/api/admin/members/${id}`,
    resetPassword: (id: Id) => `/api/admin/members/${id}/reset-password`,
    export: () => '/api/admin/members/export',
  },

  subscriptions: {
    list: () => '/api/admin/subscriptions',
    batchDelete: () => '/api/admin/subscriptions/batch-delete',
    byId: (id: Id) => `/api/admin/subscriptions/${id}`,
    cancel: (id: Id) => `/api/admin/subscriptions/${id}/cancel`,
    cancelAtRenewal: (id: Id) => `/api/admin/subscriptions/${id}/cancel-at-renewal`,
    payments: (id: Id) => `/api/admin/subscriptions/${id}/payments`,
    renewals: () => '/api/admin/subscriptions/renewals',
    export: () => '/api/admin/subscriptions/export',
    checkNumber: () => '/api/admin/subscriptions/check-number',
    members: (id: Id) => `/api/admin/subscriptions/${id}/members`,
    member: (id: Id, memberId: Id) => `/api/admin/subscriptions/${id}/members/${memberId}`,
  },

  payments: {
    byId: (id: Id) => `/api/admin/payments/${id}`,
  },

  condominiums: {
    list: () => '/api/admin/condominiums',
    batchDelete: () => '/api/admin/condominiums/batch-delete',
    byId: (id: Id) => `/api/admin/condominiums/${id}`,
    checkDuplicate: () => '/api/admin/condominiums/check-duplicate',
    merge: (id: Id) => `/api/admin/condominiums/${id}/merge`,
  },

  entities: {
    list: () => '/api/admin/entities',
    batchDelete: () => '/api/admin/entities/batch-delete',
    byId: (id: Id) => `/api/admin/entities/${id}`,
  },

  entityTypes: {
    list: () => '/api/admin/entity-types',
    byId: (id: Id) => `/api/admin/entity-types/${id}`,
  },

  departments: {
    list: () => '/api/admin/departments',
    byId: (id: Id) => `/api/admin/departments/${id}`,
  },

  antennas: {
    list: () => '/api/admin/antennas',
    byId: (id: Id) => `/api/admin/antennas/${id}`,
  },

  subscriptionTypes: {
    list: () => '/api/admin/subscription-types',
    byId: (id: Id) => `/api/admin/subscription-types/${id}`,
  },

  rnic: {
    search: () => '/api/admin/rnic/search',
    browse: () => '/api/admin/rnic/browse',
    map: () => '/api/admin/rnic/map',
    export: () => '/api/admin/rnic/export',
  },

  apiTokens: {
    list: () => '/api/admin/api-tokens',
    byId: (id: Id) => `/api/admin/api-tokens/${id}`,
  },

  import: {
    preview: () => '/api/admin/import/preview',
    /** Aperçu calculé en arrière-plan : état, avancement, résultat une fois terminé. */
    previewOf: (previewId: Id) => `/api/admin/import/previews/${previewId}`,
    /** Aperçu à reprendre en arrivant sur l'import (en cours, ou terminé non exécuté). */
    previewCurrent: () => '/api/admin/import/previews/current',
    previewRelaunch: (previewId: Id) => `/api/admin/import/previews/${previewId}/relaunch`,
    execute: () => '/api/admin/import/execute',
    /** Import lancé : avancement, puis rapport gardé sur le lot. */
    batch: (batchId: Id) => `/api/admin/import/batches/${batchId}`,
    /** Relance d'un import en échec ou interrompu, depuis le fichier conservé. */
    relaunch: (batchId: Id) => `/api/admin/import/batches/${batchId}/relaunch`,
    template: () => '/api/admin/import/template',
  },

  /** Traitements en arrière-plan : historique et flux temps réel (SSE). */
  operations: {
    list: () => '/api/admin/operations',
    events: () => '/api/admin/operations/events',
  },

  /** Revue des brouillons d'import (#137), administrateurs seulement. */
  drafts: {
    batches: () => '/api/admin/drafts',
    count: () => '/api/admin/drafts/count',
    forRecord: () => '/api/admin/drafts/record',
    batch: (batchId: Id) => `/api/admin/drafts/batches/${batchId}`,
    line: (batchId: Id, line: Id) => `/api/admin/drafts/batches/${batchId}/lines/${line}`,
    validate: (batchId: Id, line: Id) => `/api/admin/drafts/batches/${batchId}/lines/${line}/validate`,
    validateAll: (batchId: Id) => `/api/admin/drafts/batches/${batchId}/validate-all`,
    cancel: (batchId: Id) => `/api/admin/drafts/batches/${batchId}/cancel`,
    operation: (batchId: Id) => `/api/admin/drafts/batches/${batchId}/operation`,
  },

  /** Historique des versions d'une fiche et sa restauration (#135). */
  versions: {
    list: (type: VersionRecordType, id: Id) => `/api/admin/${VERSION_ROUTE[type]}/${id}/versions`,
    restore: (type: VersionRecordType, id: Id, version: number) => `/api/admin/${VERSION_ROUTE[type]}/${id}/versions/${version}/restore`,
  },

  audit: () => '/api/admin/audit',
  /** Journal transverse, reserve aux administrateurs. */
  auditJournal: () => '/api/admin/audit/journal',
  /** Journal des connexions adhérents (#159), reserve aux administrateurs. */
  connexions: {
    list: () => '/api/admin/connexions',
    export: () => '/api/admin/connexions/export',
    adherents: () => '/api/admin/connexions/adherents',
  },
  stats: () => '/api/admin/stats',
  /** Page « Statistiques » : chiffres du jour, mouvements, séries, répartitions, détail. */
  statistics: {
    overview: () => '/api/admin/stats/overview',
    movements: () => '/api/admin/stats/movements',
    series: () => '/api/admin/stats/series',
    breakdown: () => '/api/admin/stats/breakdown',
    details: () => '/api/admin/stats/details',
    exportCsv: () => '/api/admin/stats/export.csv',
    reportPdf: () => '/api/admin/stats/report.pdf',
  },
  postalSearch: () => '/api/admin/postal/search',
  exportFull: () => '/api/admin/export/full',
  ssoApps: () => '/api/sso/apps',
  ssoUsers: () => '/api/sso/users',
  openApiSpec: () => '/api/v1/openapi.json',
} as const
